import { Unit, UnitService, get_space_estimate } from './../../services/unit.service';
import { Component, AfterViewInit, OnChanges, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, SimpleChanges, ElementRef } from '@angular/core';
import * as THREE from 'three'
import { load_room } from './gltfLoaders/roomLoaders'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import gsap from 'gsap'
import { Scene, } from 'three';
import { glUtil } from './lib/context';
import { furnitureAnimationSequence, setVisibleFalse } from './gltfLoaderHelpers/animationHelper'


@Component({
	selector: 'ras-unit-interactive-view',
	templateUrl: './unit-interactive-view.component.html',
	styleUrls: ['./unit-interactive-view.component.sass']
})
export class UnitInteractiveViewComponent implements AfterViewInit, OnInit, OnDestroy, OnChanges {

	@Output() close = new EventEmitter()
	@Output() next_unit = new EventEmitter<Number>()
	@Output() unit_index = new EventEmitter<Number>()
	@Input() unit: Unit
	@ViewChild('canvas') canvas: ElementRef<HTMLCanvasElement>
	@Input() all_units: null | string[]
	@Input() agnostic_units: null | string
	@Input() agnostic_area: null | string
	@Input() agnostic_index: number
	@Input() next: Unit | null
	@Input() prev: Unit | null


	public gl: WebGLRenderingContext = null
	public gl_extention: WEBGL_lose_context = null

	public is_loading: boolean = true
	public scene: Scene = new THREE.Scene()
	public path: string = '/assets/models/'
	public model_path: string = null
	public index: number = 0
	public arrow_dimensions: string = null
	public squared_dimensions: Number = 0
	public last: boolean = false
	public destroyed: boolean = false
	public resize_listener = null
	public stand_alone_flag = false
	public show_unit_size1 = false
	public show_unit_size2 = false
	public show_unit_size3 = false
	public show_menu = false
	public description = ["For those extra special small things.", " Can store contents of an extra large cupboard.", "Can store contents of one small room.", "Can store contents of two small rooms.",
		"Can store contents of three small rooms.", " Can store contents of two or three small rooms.", " Can store contents of four small rooms.", "Can store contents of five small rooms.",
		"Can store stock of business inventory and a vehicle or contents of about nine rooms.", "Can store stock of business inventory or contents of about nine rooms.", "Massive unit for business inventory and equipments.",]

	public default_styles = {
		'min-width': '13.65%',
		'height': '53px',
		'border': '1px solid #8f8f8f',
		'background-clip': 'padding-box',
		'-webkit-background-clip': 'padding-box',
		'margin': '10px 15px',
	}
	public changed_styles = {
		'min-width': '13.65%',
		'max-height': '400px',
		'margin': '10px 15px',
		'background': '#0D874F',

	}
	public changed_p_styles = {
		'font-weight': '600',
		'color': 'white',
	}
	public default_p_styles = {
		'font-weight': '400',
		'color': 'rgba(0, 0, 0, .5)',
	}
	public default_arrow_style = {
		'position': 'fixed',
		'top': '0',
		'bottom': '0',
		'left': (window.innerWidth > 1360) ? '17.5%' : (window.innerWidth <= 1360 && window.innerWidth > 768) ? '29%' : (window.innerWidth <= 768) ? '300px' : '300px',
		'width': '2%',
		'background': 'transparent',
	}
	public changed_arrow_style = {
		'position': 'fixed',
		'top': '0',
		'bottom': '0',
		'left': '0%',
		'width': '20px',
		'background': 'white',
	}
	public default_svg_style = {
		'display': 'table-cell',
		'text-align': 'center',
		'vertical-align': 'middle',
		'height': '100%',
		'transform': 'rotate(0deg)',
	}
	public changed_svg_style = {
		'display': 'table-cell',
		'text-align': 'center',
		'margin-left': '10px',
		'vertical-align': 'middle',
		'height': '100%',
		'transform': 'rotate(180deg)',
	}
	public style_index: null | number = null

	constructor(private unitService: UnitService) {
	}

	ngOnDestroy(): void {
		this.destroyed = true
		if (this.resize_listener) window.removeEventListener('resize', this.resize_listener)
		this.scene.clear()
	}


	ngOnChanges(element: SimpleChanges) {
		if (this.agnostic_units != null) {
			this.agnostic_units = element.agnostic_units.currentValue
			let dims = this.agnostic_units.split("x")
			this.model_path = dims[0] + 'x' + dims[1] + '.gltf'
			this.arrow_dimensions = this.agnostic_area
			if (element.agnostic_units.firstChange == false) {
				this.scene.clear()
				let canvas = this.canvas.nativeElement
				this.is_loading = true
				this.loadAssets(this.path + this.model_path,
					// done
					(assets) => {
						this.is_loading = false
					},
					this.scene
				)
			}

		}
	}
	next_emit(number){
		this.next_unit.emit(number)
		this.gl_extention.loseContext()
	}

	get_estimate(unit: Unit): string {
		return get_space_estimate(unit)
	}

	quick_quote_click() {
		if ((<any>window)?.dataLayer) (<any>window).dataLayer.push({
			'event': '3d_view_quick_quote'
		});

		this.close.emit()
	}

	ngOnInit(): void {

		this.style_index = this.agnostic_index
		// ensure that the component accepts only agnostic units when triggered from the home page banner
		if (this.unit != undefined) {
			let dims = this.unit.dim.split("x").map(item => item.trim())
			this.model_path = dims[0] + 'x' + dims[1] + '.gltf'
			this.arrow_dimensions = dims[0] + ' x ' + dims[1]
			this.squared_dimensions = this.unit.area
			this.stand_alone_flag = false
		} else {
			let dims = this.agnostic_units.split("x")
			this.model_path = dims[0] + 'x' + dims[1] + '.gltf'
			this.arrow_dimensions = this.agnostic_area
			this.stand_alone_flag = true
			this.open_category(this.agnostic_index)
		}
	}

	set_index_change(index) {
		this.unit_index.emit(index)
		this.style_index = index
	}

	open_category(unit_index) {
		if (unit_index >= 0 && unit_index <= 2) {
			this.show_unit_size1 = true
		} else if (unit_index >= 3 && unit_index <= 6) {
			this.show_unit_size2 = true
		} else if (unit_index >= 7 && unit_index <= 10) {
			this.show_unit_size3 = true
		}
	}

	//manual context lost
	webgl_context_lost(e) {
		e.preventDefault()
	}

	ngAfterViewInit(): void {

		let canvas = this.canvas.nativeElement

		this.gl = glUtil.checkWebGL(canvas, { powerPreference: 'low-power' })
		this.gl_extention = this.gl.getExtension('WEBGL_lose_context')

		setTimeout(() => {
			if(this.gl.isContextLost()){this.gl_extention.restoreContext()}

			this.menu_arrow_styling(canvas)

			this.init_scene()

			this.is_loading = true

			this.loadAssets(this.path + this.model_path,
				// done
				(assets) => {
					this.is_loading = false
				},
				this.scene
			)
		}, 1);

		canvas.addEventListener("webglcontextlost", this.webgl_context_lost, false);

	}

	menu_arrow_styling(canvas) {

		let x = window.innerWidth

		window.addEventListener("resize", () => {
			this.default_arrow_style.left = (x > 1360) ? '17.5%' : (x <= 1360 && x > 768) ? '29%' : (x <= 768) ? '300px' : '300px'
		})

	}

	init_scene() {
	}

	loadAssets(path, done, scene) {
		const cursor = {
			x: 0,
			y: 0,
		}
		const snap_back_delay = 3000;
		let params = {
			directionallight1color: 0xffffff,
			backwallColor: '#abbfb5',

		}
		let canvas = this.canvas.nativeElement

		//3d loaders
		let loaded = false

		/**
		 * Animation Tweens
		 */
		const furnitureTimeline = gsap.timeline()

		/**
		 * Loading 3D asset into the this.scene
		 */

		load_room(path,
			// done
			(furnitureItemsInstance: any) => {
				if (furnitureItemsInstance.unit == null) {
					this.close.emit();
					return;
				}
				loaded = true
				scene.add(furnitureItemsInstance.unit)
				this.pack_room(furnitureItemsInstance.items, furnitureTimeline)
				done()
				furnitureItemsInstance = null
			},
			// progress
			(loaded, total, computable) => {
			}
		)
		/**
		 * Renderer Size
		 */
		const sizes = {
			width: window.innerWidth,
			height: window.innerHeight
		}
		/**
		 * Camera Configuration
		 */
		let planeAspectRatio = 16 / 9
		let camera_aspect_ratio = sizes.width / sizes.height
		const fov = 55
		const camera = new THREE.PerspectiveCamera(fov, camera_aspect_ratio, 0.1, 100)

		camera.updateProjectionMatrix()
		scene.add(camera)

		const ambientLight = new THREE.AmbientLight(0xffffff, 0.15)
		scene.add(ambientLight)

		const directionallight1 = new THREE.DirectionalLight(params.directionallight1color, 0.6)
		directionallight1.position.set(0, 7.9, 5.28)
		directionallight1.castShadow = true
		directionallight1.shadow.mapSize.height = 1024 * 4
		directionallight1.shadow.mapSize.width = 1024 * 4
		directionallight1.shadow.normalBias = 0.02
		const targetObject = new THREE.Object3D()
		targetObject.position.set(1.5, 1.5, 3)
		scene.add(targetObject)
		directionallight1.target = targetObject
		scene.add(directionallight1)

		// Orbital Camera Controls
		const controls = new OrbitControls(camera, canvas)
		const targetXYZ = { x: 1.0, y: 1.1, z: -0.5, }
		const renderer = new THREE.WebGLRenderer({
			canvas: canvas,
			antialias: true,
			alpha: true,
		})
		let max_distance = 11

		let camera_controller = (max_distance) => {
			let spring = () => gsap.timeline({ paused: true }).to(controls, {
				duration: 0.5, minPolarAngle: Math.PI * 0.29,
				maxPolarAngle: Math.PI * 0.29, minAzimuthAngle: Math.PI * 1.7, maxAzimuthAngle: Math.PI * 1.7,
				minDistance: max_distance, maxDistance: max_distance
			})

			let snap_back_timeout = null
			controls.addEventListener('end', () => {
				if (null === snap_back_timeout) {
					snap_back_timeout = setTimeout(() => { spring().play() }, snap_back_delay)
				}
			});

			controls.addEventListener('start', () => {
				if (null !== snap_back_timeout) {
					clearTimeout(snap_back_timeout)
					snap_back_timeout = null
				}
			});
			controls.addEventListener('change', () => {
				controls.minAzimuthAngle = Math.PI * 1.5 - cursor.x
				controls.maxAzimuthAngle = Math.PI * 2.05 - cursor.x
				controls.maxPolarAngle = Math.PI * 0.5
				controls.minPolarAngle = Math.PI * 0.1
				controls.minDistance = 2.5
				controls.maxDistance = max_distance

			});

			canvas.addEventListener('mousemove', event => {
				cursor.x = event.clientX / sizes.width - 0.5
				cursor.y = event.clientY / sizes.height - 0.5
				controls.minAzimuthAngle = Math.PI * 1.7 - cursor.x
				controls.maxAzimuthAngle = Math.PI * 1.7 - cursor.x
				controls.maxPolarAngle = Math.PI * 0.29 - cursor.y
				controls.minPolarAngle = Math.PI * 0.29 - cursor.y
				controls.update()
			})

			controls.enableDamping = true
			controls.dampingFactor = 0.1
			controls.maxPolarAngle = Math.PI * 0.29
			controls.minPolarAngle = Math.PI * 0.29
			//controls.minAzimuthAngle = Math.PI * 1.7 -cursor.x
			//controls.maxAzimuthAngle = Math.PI * 1.7 -cursor.x
			controls.target.set(targetXYZ.x, targetXYZ.y, targetXYZ.z)
			controls.enableZoom = true
			controls.minDistance = max_distance
			controls.maxDistance = max_distance
			controls.rotateSpeed = 0.15
			controls.zoomSpeed = 1
			controls.enablePan = false
		}

		camera.aspect > planeAspectRatio ?

			// window too large
			(camera_controller(max_distance), controls.update())
			:

			// window too narrow
			(camera_controller(max_distance + 7), controls.update())

		scene.background = new THREE.Color(params.backwallColor)
		renderer.shadowMap.enabled = true
		renderer.shadowMap.type = THREE.PCFSoftShadowMap
		renderer.setSize(sizes.width, sizes.height)
		renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
		renderer.toneMapping = THREE.ACESFilmicToneMapping
		renderer.toneMappingExposure = 1.3
		renderer.outputEncoding = THREE.sRGBEncoding

		let changed = true;

		let resize_3d_object = () => {

			sizes.width = window.innerWidth
			sizes.height = window.innerHeight
			camera.aspect = sizes.width / sizes.height
			renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
			renderer.setSize(sizes.width, sizes.height)
			camera.updateProjectionMatrix()
			changed = true;
			renderer.render(scene, camera)

			camera.aspect >= planeAspectRatio ?

				// window too large
				camera_controller(max_distance)

				:

				// window too narrow
				camera_controller(max_distance + 7)

		}

		this.resize_listener = window.addEventListener('resize', resize_3d_object)

		const clock = new THREE.Clock()

		const tick = () => {
			//this.state.animation = requestAnimationFrame(tick)
			if (loaded == false) {
				if (!this.destroyed) requestAnimationFrame(tick);
				return;
			}
			const elapsedTime = clock.getElapsedTime()

			if (changed) renderer.render(scene, camera);
			if (!this.destroyed) window.requestAnimationFrame(tick)
		}
		tick()
	}

	render_frame() {
		if (!this.destroyed) requestAnimationFrame(this.render_frame)
	}

	pack_room(furnitureItems, animationTimeline) {

		let start_packing = () => {

			if (!animationTimeline.isActive()) {

				for (let item of furnitureItems) {

					setVisibleFalse(item)
					furnitureAnimationSequence(item, animationTimeline)
				}
			}
		}

		let pack_button = (start_packing) => {

			//pack room on init
			start_packing()

			//pack on button pressed
			document.getElementById("pack").addEventListener("click", function (e) {

				start_packing()
			});
		}

		//pack room when button is pressed
		!this.agnostic_units ? pack_button(start_packing) : start_packing()


	}

}
